import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const KEY = 'learn-kamailio-progress-v1';
const ProgressContext = createContext(null);

function load() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const data = JSON.parse(raw);
      return { done: data.done || {}, quiz: data.quiz || {} };
    }
  } catch (e) {
    /* storage unavailable */
  }
  return { done: {}, quiz: {} };
}

export function ProgressProvider({ children }) {
  const [state, setState] = useState(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable: progress lasts for this visit only */
    }
  }, [state]);

  const key = (courseId, lessonId) => `${courseId}/${lessonId}`;

  const isDone = useCallback((c, l) => !!state.done[key(c, l)], [state]);
  const toggleDone = useCallback((c, l) => {
    setState((s) => {
      const done = { ...s.done };
      if (done[key(c, l)]) delete done[key(c, l)];
      else done[key(c, l)] = Date.now();
      return { ...s, done };
    });
  }, []);
  const markDone = useCallback((c, l) => {
    setState((s) => (s.done[key(c, l)] ? s : { ...s, done: { ...s.done, [key(c, l)]: Date.now() } }));
  }, []);
  const courseDone = useCallback(
    (course) => course.lessons.filter((l) => state.done[key(course.id, l.id)]).length,
    [state]
  );
  const quizScore = useCallback((courseId) => state.quiz[courseId], [state]);
  const saveQuiz = useCallback((courseId, score) => {
    setState((s) => ({ ...s, quiz: { ...s.quiz, [courseId]: Math.max(score, s.quiz[courseId] || 0) } }));
  }, []);
  const reset = useCallback(() => setState({ done: {}, quiz: {} }), []);

  const value = useMemo(
    () => ({ isDone, toggleDone, markDone, courseDone, quizScore, saveQuiz, reset, doneCount: Object.keys(state.done).length }),
    [isDone, toggleDone, markDone, courseDone, quizScore, saveQuiz, reset, state.done]
  );
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export const useProgress = () => useContext(ProgressContext);
